'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Link from 'next/link';

import { EndMark } from '@/app/home-solar/news/EndMark';
import { GetStartedCta, Subscribe } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

const quoteUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://quote.aurorasolar.com';

export const MarketplaceLaunchRelease = () => (
  <article>
    <DS.Flex gap={5} maxW={800} mx="auto" px={{ _: 4, md: 6 }} py={{ _: 7, md: 9 }}>
      <DS.Flex alignItems="center" flexDirection="row" gap={1}>
        <DS.Button as={Link} color="uiLink" href="/home-solar/news" variant="text">
          News
        </DS.Button>
        <span>/</span>
        <DS.Text color="uiHelperCopy">Press Release · August 4, 2026</DS.Text>
      </DS.Flex>

      <DS.Text as="h1" text={{ _: 'h1', md: 'd1' }}>
        Aurora Solar Launches Aurora Solar Marketplace to Compare Local Installers and Costs
      </DS.Text>
      <DS.Text color="uiHelperCopy" text="body20">
        Homeowners receive an estimate built for their own home with personalized solar and storage offers side by side
        &mdash; costs, equipment, financing, and reviews &mdash; all without sharing their phone number, so they can
        take the next step when they&rsquo;re ready.
      </DS.Text>

      <DS.Divider />

      <p>
        <strong>August 4, 2026 &ndash; SAN FRANCISCO</strong>
        <span>
          {' '}
          &ndash; Aurora Solar today introduced the Aurora Solar Marketplace, an online marketplace where homeowners get
          a personalized solar and storage estimate and compare local installers on their own timeline, without sharing
          their phone number. It&rsquo;s backed by Aurora Solar, the platform powering the energy revolution for 7,000+
          solar professionals, used by 7 in 10 top U.S. solar contractors, and behind 20 million+ solar projects since
          2013.
        </span>
      </p>
      <p>
        Home energy has never felt less certain, with prices climbing and a strained grid putting steady, affordable
        power out of reach. Homeowners are realizing that every gas or electric bill is really just renting their power
        &mdash; many would rather own it outright, making it themselves with solar. So, what&rsquo;s holding them back?
        According to Aurora Solar&rsquo;s 2026 research, trust is the second-biggest barrier to going solar, only behind
        cost.
      </p>
      <p>
        It&rsquo;s not hard to see why. Most often, homeowners have to hand over their contact information before they
        even see an estimate, then compare competing proposals with no easy way to tell which is fair. The Aurora Solar
        Marketplace changes that, empowering homeowners to:
      </p>

      <x.ul display="flex" flexDirection="column" gap={3} listStyleType="disc" pl={5}>
        <li>
          <strong>Personalize research and estimates:</strong>
          <span>
            {' '}
            Homeowners enter their address and get an estimate built on their own roof and electricity usage, not a
            regional average. They can resize the system, add battery storage, and see how their estimated savings
            change. Because it runs on the same platform installers use to design real systems, the estimate reflects
            how the system gets built.
          </span>
        </li>
        <li>
          <strong>Compare local installers on what actually matters:</strong>
          <span>
            {' '}
            Each installer serving the area appears side by side, with the details that drive a real decision: long-term
            savings, system size, equipment, financing, and independent ratings. Aurora Solar doesn&rsquo;t rank,
            endorse, or hide installers.
          </span>
        </li>
        <li>
          <strong>Reach out when they&rsquo;re ready:</strong>
          <span>
            {' '}
            No contact information required to look. Homeowners decide who to talk to and when, then connect with the
            installer they feel most comfortable with. Aurora Solar doesn&rsquo;t sell or install solar; it gives
            homeowners a trustworthy place to start.
          </span>
        </li>
      </x.ul>

      <p>
        &ldquo;The U.S. needs every electron it can get, and homeowners want to take control of their power instead of
        just paying more for it,&rdquo; said Chris Hopper, Co-Founder and Executive Chairman, Aurora Solar. &ldquo;With
        so much solar energy uncollected from rooftops, the Aurora Solar Marketplace empowers people to see what solar
        and storage means for their own home on their own terms, delivered by the company the industry already relies on
        to design and price solar.&rdquo;
      </p>
      <p>This kind of clarity hits home with solar shoppers.</p>
      <p>
        &ldquo;I am a very price-sensitive person, so I was particularly concerned about that &mdash; but I was able to
        do a lot of research on different solar products with the Aurora Solar Marketplace,&rdquo; said Richard, who
        went solar in Georgia using the Aurora Solar Marketplace. &ldquo;It&rsquo;s not just about saving money. When
        you see it and it looks nice, that helps too.&rdquo;
      </p>
      <p>
        <span>The Aurora Solar Marketplace is available* for homeowners at </span>
        <x.a color="uiLink" href={quoteUrl}>
          quote.aurorasolar.com
        </x.a>
        <span>.</span>
      </p>

      <DS.Text as="h2" mt={4} text="h2">
        About Aurora Solar
      </DS.Text>
      <p>
        <span>
          Aurora Solar is the platform powering the energy revolution. The company puts the power of data, automation,
          and AI into the hands of every solar professional to make solar adoption simple and predictable. Aurora
          Solar&rsquo;s cloud-based platform has been used to design more than 20 million solar projects globally, and
          more than 7,000 of the industry&rsquo;s top organizations rely on it to streamline workflows and grow their
          businesses. Aurora Solar has been recognized by TIME as a Top U.S. GreenTech Company (2024&ndash;2026), named
          to the 2025 Inc 5000 &amp; 2024 Deloitte Fast 500, and listed among Solar Power World&rsquo;s Top Solar
          Software &amp; Monitoring Products in 2024. The company is headquartered in San Francisco. For more
          information, visit{' '}
        </span>
        <x.a as={Link} color="uiLink" href="/home-solar/about-the-marketplace">
          aurorasolar.com/home-solar/about-the-marketplace
        </x.a>
        <span>.</span>
      </p>

      <DS.Text as="h2" mt={4} text="h2">
        Contact
      </DS.Text>
      <DS.Flex gap={1}>
        <span>Karen DeVincent-Reinbold PR &amp; communications director</span>
        <x.a color="uiLink" href="mailto:press@aurorasolar.com">
          press@aurorasolar.com
        </x.a>
      </DS.Flex>

      <DS.Text color="uiHelperCopy" mt={4} text="body12">
        <strong>*ESTIMATES ARE NON-BINDING AND CONFIRMED BY THE INSTALLER AFTER A SITE VISIT</strong>
      </DS.Text>

      <EndMark />
    </DS.Flex>

    <GetStartedCta eventName={TRACKING.NEWS_GET_QUOTE}>
      <Subscribe buttonText="Subscribe" variant="primary" />
    </GetStartedCta>
  </article>
);
